import IconRenderer, { NavigationIcon } from '@/ui/Layout/Sidebar/IconRenderer';
import StyledNavItem from '@/ui/Layout/Sidebar/StyledComponents/StyledNavItem';
import {
  normalizeFavoritePath,
  stripFavoriteSearch,
} from '@/utils/favorites/path';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import { Box, Link, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface NavigationItem {
  label: string;
  url?: string;
  icon?: NavigationIcon;
  permission?: string;
  isDrawer?: boolean;
  items?: NavigationItem[];
  disabled?: boolean;
  badge?: string | number;
  roles?: string[];
  external?: boolean;
  favorite?: boolean;
}

export interface SidebarItemProps {
  item: NavigationItem;
  depth: number;
  collapsed: boolean;
  currentPath: string;
  onItemClick: (item: NavigationItem) => void;
  isLastChild?: boolean;
  onExpandSidebar?: () => void;
}

const isItemActive = (url = '', currentPath = '') => {
  if (!url || !currentPath) return false;

  let normalizedUrl = '';
  let normalizedCurrentPath = '';

  try {
    normalizedUrl = normalizeFavoritePath(url);
    normalizedCurrentPath = normalizeFavoritePath(currentPath);
  } catch (error) {
    return false;
  }

  if (normalizedUrl === normalizedCurrentPath) return true;
  if (normalizedUrl.includes('?')) return false;

  const urlPathname = stripFavoriteSearch(normalizedUrl);
  const currentPathname = stripFavoriteSearch(normalizedCurrentPath);

  if (urlPathname === currentPathname) return true;
  if (currentPathname.startsWith(urlPathname + '/')) return true;

  return false;
};

interface TruncatedNavLabelProps {
  label: string;
  sx?: SxProps<Theme>;
}

const TruncatedNavLabel: React.FC<TruncatedNavLabelProps> = ({ label, sx }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const measureTruncation = useCallback(() => {
    const element = textRef.current;
    if (!element) {
      return;
    }
    setIsTruncated(element.scrollWidth > element.clientWidth);
  }, []);

  useLayoutEffect(() => {
    measureTruncation();
  }, [label, measureTruncation]);

  useEffect(() => {
    window.addEventListener('resize', measureTruncation);
    return () => {
      window.removeEventListener('resize', measureTruncation);
    };
  }, [measureTruncation]);

  return (
    <Tooltip
      title={isTruncated ? label : ''}
      disableHoverListener={!isTruncated}
      enterDelay={500}
    >
      <ListItemText
        primary={label}
        sx={{
          margin: 0,
          minWidth: 0,
          ...sx,
        }}
        primaryTypographyProps={{
          ref: textRef,
          component: 'span',
          sx: {
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
      />
    </Tooltip>
  );
};

const SidebarItem: React.FC<SidebarItemProps> = ({
  item,
  depth,
  collapsed,
  currentPath,
  onItemClick,
  isLastChild = false,
  onExpandSidebar,
}) => {
  const [open, setOpen] = useState(false);
  const isActive = isItemActive(item.url, currentPath);
  const hasChildren = Boolean(item.items?.length);
  const isDrawer = item.isDrawer || hasChildren;

  const handleClick = useCallback(() => {
    if (isDrawer) {
      setOpen(!open);
    } else {
      if (!item.external) {
        onItemClick(item);
      }
    }
  }, [isDrawer, open, item, onItemClick]);

  const handleChildClick = useCallback(
    (childItem: NavigationItem) => {
      onItemClick(childItem);
    },
    [onItemClick]
  );

  const hasActiveDescendant = useMemo(() => {
    const checkActiveDescendant = (items: NavigationItem[] = []): boolean => {
      return items.some((child) => {
        if (isItemActive(child.url, currentPath)) return true;
        return checkActiveDescendant(child.items);
      });
    };
    return hasChildren && checkActiveDescendant(item.items);
  }, [hasChildren, item.items, currentPath]);

  const iconIsActive = depth === 0 ? isActive || hasActiveDescendant : isActive;
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (hasActiveDescendant) {
      setOpen(true);
    }
  }, [hasActiveDescendant]);

  if (collapsed && depth === 0) {
    const handleCollapsedClick = (event: React.MouseEvent) => {
      event.stopPropagation();
      if (hasChildren) {
        if (typeof onExpandSidebar === 'function') {
          onExpandSidebar();
        }
        setOpen(true);
      } else {
        if (!item.external) {
          if (typeof onExpandSidebar === 'function') {
            onExpandSidebar();
          }
          onItemClick(item);
        }
      }
    };
    return (
      <Tooltip title={item.label} placement='right' arrow>
        <Box
          component='li'
          sx={{
            p: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 44,
            position: 'relative',
            listStyle: 'none',
            cursor: 'pointer',
            bgcolor: hovered ? 'action.hover' : 'inherit',
            transition: 'background 0.2s',
          }}
          onClick={handleCollapsedClick}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {item.icon && (
            <ListItemIcon
              sx={{
                minWidth: 0,
                justifyContent: 'center',
                color: 'inherit',
              }}
            >
              <IconRenderer icon={item.icon} active={iconIsActive} size={24} />
            </ListItemIcon>
          )}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box
      component='li'
      sx={{
        position: 'relative',
        listStyle: 'none',
        pl: depth > 0 ? 3 : 0,
        ...(depth > 0 && {
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -2,
            bottom: isLastChild ? (open ? '95%' : '60%') : 0,
            width: '2px',
            bgcolor: '#e0e0e0',
            borderRadius: 1,
            zIndex: 0,
          },
        }),
      }}
    >
      {item.external ? (
        <Link
          href={item.url}
          target='_blank'
          rel='noopener noreferrer'
          underline='none'
          sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
          aria-label={collapsed ? item.label : undefined}
        >
          <StyledNavItem
            active={iconIsActive}
            depth={depth}
            collapsed={collapsed}
            hasActiveDescendant={hasActiveDescendant}
            disabled={item.disabled}
            isLastChild={isLastChild}
          >
            {item.icon && (
              <ListItemIcon
                sx={{
                  minWidth: collapsed ? 0 : 40,
                  justifyContent: 'center',
                  color: 'inherit',
                }}
              >
                <IconRenderer
                  icon={item.icon}
                  active={iconIsActive}
                  size={depth > 0 ? 20 : 24}
                />
              </ListItemIcon>
            )}
            {!collapsed && (
              <>
                <TruncatedNavLabel
                  label={item.label}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: depth > 0 ? '0.8125rem' : '0.875rem',
                      fontWeight:
                        isActive || hasActiveDescendant
                          ? (theme) => theme.typography.fontWeightBold
                          : (theme) => theme.typography.fontWeightMedium,
                      lineHeight: 1.4,
                    },
                  }}
                />
                {item.badge && (
                  <Box
                    sx={{
                      backgroundColor: (theme) => theme.palette.error.main,
                      color: (theme) => theme.palette.error.contrastText,
                      borderRadius: '50%',
                      minWidth: depth > 0 ? 16 : 20,
                      height: depth > 0 ? 16 : 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: depth > 0 ? '0.6875rem' : '0.75rem',
                      fontWeight: 600,
                      marginLeft: 1,
                      flexShrink: 0,
                    }}
                  >
                    {item.badge}
                  </Box>
                )}
                {isDrawer && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      marginLeft: 0.5,
                      color: 'inherit',
                      opacity: 0.7,
                    }}
                  >
                    {open ? <ExpandMore /> : <ChevronRight />}
                  </Box>
                )}
              </>
            )}
          </StyledNavItem>
        </Link>
      ) : (
        <StyledNavItem
          active={iconIsActive}
          depth={depth}
          collapsed={collapsed}
          hasActiveDescendant={hasActiveDescendant}
          disabled={item.disabled}
          onClick={handleClick}
          isLastChild={isLastChild}
          aria-label={collapsed ? item.label : undefined}
        >
          {item.icon && (
            <ListItemIcon
              sx={{
                minWidth: collapsed ? 0 : 40,
                justifyContent: 'center',
                color: 'inherit',
              }}
            >
              <IconRenderer
                icon={item.icon}
                active={iconIsActive}
                size={depth > 0 ? 20 : 24}
              />
            </ListItemIcon>
          )}
          {!collapsed && (
            <>
              <TruncatedNavLabel
                label={item.label}
                sx={{
                  '& .MuiListItemText-primary': {
                    fontSize: depth > 0 ? '0.8125rem' : '0.875rem',
                    fontWeight:
                      isActive || hasActiveDescendant
                        ? (theme) => theme.typography.fontWeightBold
                        : (theme) => theme.typography.fontWeightMedium,
                    lineHeight: 1.4,
                  },
                }}
              />
              {item.badge && (
                <Box
                  sx={{
                    backgroundColor: (theme) => theme.palette.error.main,
                    color: (theme) => theme.palette.error.contrastText,
                    borderRadius: '50%',
                    minWidth: depth > 0 ? 16 : 20,
                    height: depth > 0 ? 16 : 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: depth > 0 ? '0.6875rem' : '0.75rem',
                    fontWeight: 600,
                    marginLeft: 1,
                    flexShrink: 0,
                  }}
                >
                  {item.badge}
                </Box>
              )}
              {isDrawer && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginLeft: 0.5,
                    color: 'inherit',
                    opacity: 0.7,
                  }}
                >
                  {open ? <ExpandMore /> : <ChevronRight />}
                </Box>
              )}
            </>
          )}
        </StyledNavItem>
      )}

      {/* Render children recursively as nested ul */}
      {hasChildren && !collapsed && open && (
        <Box
          component='ul'
          sx={{
            m: 0,
            p: 0,
            listStyle: 'none',
            pl: 2,
          }}
        >
          {item.items?.map((child, index, array) => (
            <SidebarItem
              key={child.label}
              item={child}
              depth={depth + 1}
              collapsed={collapsed}
              currentPath={currentPath}
              onItemClick={handleChildClick}
              isLastChild={index === array.length - 1}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SidebarItem;
